// T22.528 (Gordon): "after upgrading, return to the AI Generator with
// everything restored exactly (prompt, segment count, uploaded media, website,
// options)".
//
// Upgrade Now from the AI builder opens the plan picker; a new plan is paid on
// Stripe in a NEW tab that lands on /settings, where no builder exists. So the
// builder writes its whole state here first and the app shell
// (AiBuilderUpgradeResume) re-opens the builder from it once the plan tier is
// higher than when the draft was saved.
//
// Fields go to localStorage (shared by every tab of the origin). The attached
// image files go to IndexedDB, which stores File objects as they are; where
// IndexedDB is missing the fields still restore and the files are skipped.

export const UPGRADE_DRAFT_KEY = "iyuga.aiBuilder.upgradeDraft.v1";
export const UPGRADE_DRAFT_TTL_MS = 24 * 60 * 60 * 1000;

const DB_NAME = "iyuga-ai-builder";
const STORE = "upgradeDraftFiles";
const FILES_KEY = "files";

const openDb = () =>
  new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined" || !indexedDB) {
      reject(new Error("IndexedDB unavailable"));
      return;
    }
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

const run = async (mode, fn) => {
  const db = await openDb();
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, mode);
      const req = fn(tx.objectStore(STORE));
      tx.oncomplete = () => resolve(req?.result);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
};

export const indexedDbFileStore = {
  put: (files) => run("readwrite", (s) => s.put(files, FILES_KEY)),
  get: () => run("readonly", (s) => s.get(FILES_KEY)),
  clear: () => run("readwrite", (s) => s.delete(FILES_KEY)),
};

/**
 * Save the builder's state before the upgrade.
 * `fields` = { builder: {...}, funnel: {...}|null }; `tierAtSave` = sub_tier now.
 */
export const saveUpgradeDraft = async (
  { fields, files = [], tierAtSave, ownerId },
  fileStore = indexedDbFileStore,
  now = Date.now()
) => {
  let fileCount = 0;
  if (files.length > 0) {
    try {
      await fileStore.put(files);
      fileCount = files.length;
    } catch (e) {
      fileCount = 0;
    }
  } else {
    try {
      await fileStore.clear();
    } catch (e) {
      // nothing stored
    }
  }
  const draft = { v: 1, savedAt: now, tierAtSave, ownerId, fileCount, fields };
  try {
    localStorage.setItem(UPGRADE_DRAFT_KEY, JSON.stringify(draft));
  } catch (e) {
    return null;
  }
  return draft;
};

/** The saved draft, or null when there is none, it is unreadable or it expired. */
export const readUpgradeDraft = (now = Date.now()) => {
  let draft = null;
  try {
    draft = JSON.parse(localStorage.getItem(UPGRADE_DRAFT_KEY) || "null");
  } catch (e) {
    draft = null;
  }
  if (!draft || draft.v !== 1 || !draft.fields) return null;
  if (!(now - draft.savedAt < UPGRADE_DRAFT_TTL_MS)) {
    clearUpgradeDraft();
    return null;
  }
  return draft;
};

/** Resume only after the plan really went up (a cancelled upgrade never re-opens it). */
export const shouldResumeUpgradeDraft = (draft, currentTier) =>
  !!draft &&
  Number.isInteger(currentTier) &&
  Number.isInteger(draft.tierAtSave) &&
  currentTier > draft.tierAtSave;

export const loadUpgradeDraftFiles = async (
  draft,
  fileStore = indexedDbFileStore
) => {
  if (!draft?.fileCount) return [];
  try {
    const files = await fileStore.get();
    return Array.isArray(files) ? files : [];
  } catch (e) {
    return [];
  }
};

export const clearUpgradeDraft = (fileStore = indexedDbFileStore) => {
  try {
    localStorage.removeItem(UPGRADE_DRAFT_KEY);
  } catch (e) {
    // ignore
  }
  return Promise.resolve()
    .then(() => fileStore.clear())
    .catch(() => {});
};

/** Lowest plan that unlocks at least one restricted item (the Upgrade Now target). */
export const upgradeTierForRestrictions = (restricted) => {
  const tiers = (restricted || [])
    .map((r) => r?.required_tier)
    .filter((t) => Number.isInteger(t));
  return tiers.length ? Math.min(...tiers) : 0;
};

// Builders open in THIS tab, by id. The tab that saved the draft still has its
// builder open (the plan picker only stacks on top of it), so the app shell
// must not open a second one here.
const liveBuilders = new Set();
export const markBuilderLive = (id) => {
  liveBuilders.add(id);
  return () => liveBuilders.delete(id);
};
export const isBuilderLive = (id) => liveBuilders.has(id);
